import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@elva/platform';
import { useToast } from '@elva/ui';
import { keys } from '@/api/queryKeys';
import { readOAuthCallbackParams } from '../api/emailOauth';
import { runEmailOauthCallbackOnce } from '../api/runEmailOauthCallback';

async function invalidateEmailConnectionQueries(
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: keys.email.accounts }),
    queryClient.invalidateQueries({ queryKey: keys.email.mailAccounts }),
    queryClient.invalidateQueries({ queryKey: keys.settings.emailInteg }),
    queryClient.invalidateQueries({ queryKey: keys.email.messages() }),
  ]);
}

/**
 * Google redirects to `/settings/general-info?code=...&state=...`.
 * Finish connect by POSTing `{ provider, code, state }` to the backend once.
 */
export function useEmailOauthCallback() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const authStatus = useAuthStore((s) => s.status);
  const startedRef = useRef(false);
  const [isCompleting, setIsCompleting] = useState(() => {
    const { code, state } = readOAuthCallbackParams();
    return Boolean(code && state);
  });

  useEffect(() => {
    const { code, state, error: oauthError } = readOAuthCallbackParams();
    const hasCallbackParams = Boolean(code && state);

    if (!hasCallbackParams && !oauthError) return;
    if (authStatus !== 'authenticated') return;
    if (startedRef.current) return;
    startedRef.current = true;
    setIsCompleting(true);

    if (oauthError) {
      setIsCompleting(false);
      toast({
        title: 'Email connection failed',
        description: oauthError,
        variant: 'error',
      });
      return;
    }

    void runEmailOauthCallbackOnce()
      .then(async (result) => {
        if (result.status === 'missing') {
          toast({
            title: 'Email connection failed',
            description: 'Missing OAuth callback parameters.',
            variant: 'error',
          });
          return;
        }

        if (result.status === 'error') {
          toast({
            title: 'Email connection failed',
            description: result.message,
            variant: 'error',
          });
          return;
        }

        await invalidateEmailConnectionQueries(queryClient);
        toast({
          title: 'Gmail connected',
          description: 'Your mailbox is linked and syncing.',
          variant: 'success',
        });
      })
      .finally(() => {
        setIsCompleting(false);
      });
  }, [authStatus, queryClient, toast]);

  return { isCompletingOauth: isCompleting };
}
